import React, { useEffect, useState } from 'react'
import CompletionProjectionsModal from '../components/CompletionProjectionsModal'
import StrategicCountdownTable from '../components/StrategicCountdownTable'
import { useAuth } from '../auth/AuthProvider'
import { fetchChecklistOpsProjects, fetchChecklistOpsSummary } from '../services/opsChecklistSummaryClient'
import { fetchSalesSummary } from '../services/salesSummaryClient'
import { downloadSalesHistoryReport } from '../services/salesHistoryReportClient'
import { listAllUnits } from '../services/unitsClient'

function normalizeGroup(value) {
  return String(value || '').trim().toLowerCase()
}

const Dashboard = ({ onNavigate }) => {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()
  const [showProjectionsModal, setShowProjectionsModal] = useState(false)
  const [salesSummary, setSalesSummary] = useState({
    year: new Date().getFullYear(),
    planned_closings: 85,
    closings_ytd: null,
    backlog: null,
    for_sale: null,
    inventory: null,
    annual_ytd_rows: [],
    annual_ytd_totals: {
      sales_ytd: 0,
      closings_ytd: 0,
      revenue_ytd: 0
    }
  })
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [opsProjects, setOpsProjects] = useState([])
  const [opsProject, setOpsProject] = useState('')
  const [opsSummary, setOpsSummary] = useState({
    wip_units: null,
    on_time_pct: null,
    rain_pct: null
  })
  const [opsLoading, setOpsLoading] = useState(false)
  const [opsError, setOpsError] = useState('')
  const [salesHistoryProjects, setSalesHistoryProjects] = useState([])
  const [salesHistoryProject, setSalesHistoryProject] = useState('')
  const [salesHistoryYear, setSalesHistoryYear] = useState(String(currentYear))
  const [salesHistoryMode, setSalesHistoryMode] = useState('closings_only')
  const [salesHistoryFormat, setSalesHistoryFormat] = useState('pdf')
  const [salesHistoryLoading, setSalesHistoryLoading] = useState(false)
  const [salesHistoryError, setSalesHistoryError] = useState('')
  const salesHistoryYears = Array.from(
    { length: Math.max(0, currentYear - 2023 + 1) },
    (_, index) => currentYear - index
  )

  useEffect(() => {
    let abort = false
    async function loadSummary() {
      setSummaryLoading(true)
      try {
        const data = await fetchSalesSummary()
        if (!abort) {
          setSalesSummary(data)
        }
      } catch (error) {
        console.error('Sales summary load failed:', error)
      } finally {
        if (!abort) {
          setSummaryLoading(false)
        }
      }
    }
    loadSummary()
    return () => { abort = true }
  }, [])

  useEffect(() => {
    let abort = false
    async function loadOpsProjects() {
      try {
        const data = await fetchChecklistOpsProjects()
        if (abort) return
        const projects = data.projects || []
        setOpsProjects(projects)
        if (!opsProject && projects.length) {
          setOpsProject(projects[0])
        }
      } catch (error) {
        if (!abort) {
          console.error('Ops projects load failed:', error)
          setOpsProjects([])
        }
      }
    }
    loadOpsProjects()
    return () => { abort = true }
  }, [])

  useEffect(() => {
    let abort = false
    async function loadSalesHistoryProjects() {
      try {
        const response = await listAllUnits()
        if (abort) return
        const rawProjects = Array.from(new Set((response.items || [])
          .map(unit => String(unit.project_id || '').trim())
          .filter(Boolean)))
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        const hasSomiProjects = rawProjects.some(project => project.toLowerCase().startsWith('somi'))
        const projects = hasSomiProjects
          ? ['SoMi All', ...rawProjects]
          : rawProjects
        setSalesHistoryProjects(projects)
        if (!salesHistoryProject && projects.length) {
          setSalesHistoryProject(hasSomiProjects ? 'SoMi All' : projects[0])
        }
      } catch (error) {
        if (!abort) {
          console.error('Sales history projects load failed:', error)
          setSalesHistoryProjects([])
        }
      }
    }
    loadSalesHistoryProjects()
    return () => { abort = true }
  }, [])

  useEffect(() => {
    let abort = false
    async function loadOpsSummary() {
      if (!opsProject) return
      setOpsLoading(true)
      setOpsError('')
      try {
        const data = await fetchChecklistOpsSummary({ project_id: opsProject })
        if (!abort) {
          setOpsSummary({
            wip_units: data?.wip_units ?? null,
            on_time_pct: data?.on_time_pct ?? null,
            rain_pct: data?.rain_pct ?? null
          })
        }
      } catch (error) {
        console.error('Ops summary load failed:', error)
        if (!abort) {
          setOpsError('Ops summary unavailable')
          setOpsSummary({
            wip_units: null,
            on_time_pct: null,
            rain_pct: null
          })
        }
      } finally {
        if (!abort) {
          setOpsLoading(false)
        }
      }
    }
    loadOpsSummary()
    return () => { abort = true }
  }, [opsProject])

  function formatPercent(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return '--%'
    return `${value}%`
  }

  function formatWholeCurrency(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return '--'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value)
  }

  async function handleSalesHistoryDownload() {
    if (!salesHistoryProject) return
    setSalesHistoryLoading(true)
    setSalesHistoryError('')
    try {
      const download = await downloadSalesHistoryReport({
        project_id: salesHistoryProject,
        year: salesHistoryYear,
        mode: salesHistoryMode,
        format: salesHistoryFormat
      })
      const yearSuffix = salesHistoryYear === 'all' ? 'all-years' : salesHistoryYear
      const modeSuffix = salesHistoryMode === 'closings_only'
        ? 'closings'
        : salesHistoryMode === 'include_sold'
          ? 'closings-and-sold'
          : 'sold-only'
      const baseFileName = `sales-history-${salesHistoryProject.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${modeSuffix}-${yearSuffix}-${new Date().toISOString().slice(0, 10)}`
      const downloads = download?.kind === 'multiple'
        ? download.value
        : download
          ? [download]
          : []

      for (const item of downloads) {
        if (item?.kind !== 'blob') continue
        const url = window.URL.createObjectURL(item.value)
        const link = document.createElement('a')
        link.href = url
        link.download = `${baseFileName}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Sales history report download failed:', error)
      setSalesHistoryError(error.message || 'Sales history report failed')
    } finally {
      setSalesHistoryLoading(false)
    }
  }

  const normalizedGroups = Array.isArray(user?.groups)
    ? user.groups.map(normalizeGroup)
    : []
  const isFieldUser = user?.role === 'ops_field' || normalizedGroups.includes('field')
  const isVpUser = user?.role === 'ops_vp' || normalizedGroups.includes('vp')
  const isSalesVpUser = user?.role === 'ops_sales_vp' || normalizedGroups.includes('sales_vp')
  const canUseProjections = !isFieldUser && !isSalesVpUser
  const canUseVendors = !isSalesVpUser
  const canUseSalesManagement = !isFieldUser
  const canUseWeeklySalesForm = !isFieldUser && !isVpUser

  return (
    <div className="page">
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Unified operations overview</p>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card dashboard-card--summary">
          <div className="dashboard-card-main">
            <h3>Dashboard Pulse Check</h3>
            <div className="dashboard-card-ops">
              <div className="dashboard-card-ops-header">Operations</div>
              <div className="dashboard-card-ops-metrics">
                <div className="dashboard-ops-metric">
                  <span className="dashboard-ops-label">Project</span>
                  {opsProjects.length ? (
                    <select
                      className="dashboard-ops-select"
                      value={opsProject}
                      onChange={event => setOpsProject(event.target.value)}
                    >
                      {opsProjects.map(project => (
                        <option key={project} value={project}>{project}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="dashboard-ops-value">{opsLoading ? '…' : '--'}</span>
                  )}
                </div>
                <div className="dashboard-ops-metric dashboard-ops-metric--wip">
                  <span className="dashboard-ops-label">WIP</span>
                  <span className="dashboard-ops-value">
                    {opsLoading ? '…' : (opsSummary.wip_units ?? '--')}
                  </span>
                </div>
                <div className="dashboard-ops-metric">
                  <span className="dashboard-ops-label">On-time</span>
                  <span className="dashboard-ops-value">
                    {opsLoading ? '…' : formatPercent(opsSummary.on_time_pct)}
                  </span>
                </div>
                <div className="dashboard-ops-metric">
                  <span className="dashboard-ops-label">Rain</span>
                  <span className="dashboard-ops-value">
                    {opsLoading ? '…' : formatPercent(opsSummary.rain_pct)}
                  </span>
                </div>
              </div>
              {opsError && (
                <div style={{ marginTop: 6, color: '#b91c1c', fontSize: '0.8rem' }}>
                  {opsError}
                </div>
              )}
            </div>
          </div>
          <div className="dashboard-card-side">
            <div className="dashboard-card-metrics">
              <div className="dashboard-metric">
                <span className="dashboard-metric-label">
                  Planned Closings ({salesSummary.year})
                </span>
                <span className="dashboard-metric-value">
                  {salesSummary.planned_closings}
                </span>
              </div>
              <div className="dashboard-metric">
                <span className="dashboard-metric-label">
                  Closings YTD
                </span>
                <span className="dashboard-metric-value">
                  {summaryLoading ? '…' : (salesSummary.closings_ytd ?? '--')}
                </span>
              </div>
              <div className="dashboard-metric">
                <span className="dashboard-metric-label">
                  Current Backlog
                </span>
                <span className="dashboard-metric-value">
                  {summaryLoading ? '…' : (salesSummary.backlog ?? '--')}
                </span>
              </div>
              <div className="dashboard-metric">
                <span className="dashboard-metric-label">
                  For Sale
                </span>
                <span className="dashboard-metric-value">
                  {summaryLoading ? '…' : (salesSummary.for_sale ?? '--')}
                </span>
              </div>
              <div className="dashboard-metric">
                <span className="dashboard-metric-label">
                  Inventory
                </span>
                <span className="dashboard-metric-value">
                  {summaryLoading ? '…' : (salesSummary.inventory ?? '--')}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <h3>Operations</h3>
          <p>Operations management tools</p>
          <div className="dashboard-card-actions">
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('operations', 'milestones')}
            >
              Building Milestones
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('operations', 'checklist')}
            >
              Daily Checklist
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('operations', 'edes-milestones')}
            >
              Edes Milestones
            </button>
            {canUseProjections && (
              <button
                className="btn"
                type="button"
                onClick={() => setShowProjectionsModal(true)}
              >
                Sales / Comp. Projections
              </button>
            )}
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('timeline')}
            >
              Magnet Board
            </button>
            {canUseVendors && (
              <button
                className="btn"
                type="button"
                onClick={() => onNavigate?.('vendors')}
              >
                Vendors
              </button>
            )}
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('procurement-rfp')}
            >
              Procurement RFP
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('procurement-review')}
            >
              Procurement Review
            </button>
          </div>
        </div>

        <div className="dashboard-card dashboard-card--sales">
          <h3>Sales</h3>
          <p>Unit inventory and status tracking</p>
          <div className="dashboard-sales-rollup">
            <table className="dashboard-sales-rollup-table">
              <colgroup>
                <col className="dashboard-sales-rollup-col-year" />
                <col className="dashboard-sales-rollup-col-sales" />
                <col className="dashboard-sales-rollup-col-closings" />
                <col className="dashboard-sales-rollup-col-revenue" />
              </colgroup>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>YTD<br />Sales</th>
                  <th>YTD<br />Closings</th>
                  <th>YTD Total Rev</th>
                </tr>
              </thead>
              <tbody>
                {(salesSummary.annual_ytd_rows || []).map(row => (
                  <tr key={row.year}>
                    <td>{row.year}</td>
                    <td>{summaryLoading ? '…' : row.sales_ytd}</td>
                    <td>{summaryLoading ? '…' : row.closings_ytd}</td>
                    <td>{summaryLoading ? '…' : formatWholeCurrency(row.revenue_ytd)}</td>
                  </tr>
                ))}
                <tr className="dashboard-sales-rollup-total">
                  <td>Total</td>
                  <td>{summaryLoading ? '…' : (salesSummary.annual_ytd_totals?.sales_ytd ?? '--')}</td>
                  <td>{summaryLoading ? '…' : (salesSummary.annual_ytd_totals?.closings_ytd ?? '--')}</td>
                  <td>{summaryLoading ? '…' : formatWholeCurrency(salesSummary.annual_ytd_totals?.revenue_ytd)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="dashboard-card-actions">
            <div className="dashboard-sales-history-control">
              <label htmlFor="sales-history-project">Closings History</label>
              <div className="dashboard-sales-history-row">
                <select
                  id="sales-history-project"
                  className="dashboard-ops-select"
                  value={salesHistoryProject}
                  onChange={event => setSalesHistoryProject(event.target.value)}
                  disabled={!salesHistoryProjects.length || salesHistoryLoading}
                >
                  {salesHistoryProjects.map(project => (
                    <option key={project} value={project}>{project}</option>
                  ))}
                </select>
                <select
                  id="sales-history-year"
                  className="dashboard-ops-select"
                  value={salesHistoryYear}
                  onChange={event => setSalesHistoryYear(event.target.value)}
                  disabled={salesHistoryLoading}
                >
                  {salesHistoryYears.map(year => (
                    <option key={year} value={String(year)}>{year}</option>
                  ))}
                  <option value="all">All years</option>
                </select>
                <select
                  id="sales-history-mode"
                  className="dashboard-ops-select"
                  value={salesHistoryMode}
                  onChange={event => setSalesHistoryMode(event.target.value)}
                  disabled={salesHistoryLoading}
                >
                  <option value="closings_only">Closings only</option>
                  <option value="include_sold">Include Sold</option>
                  <option value="sold_only">Sold only</option>
                </select>
                <select
                  id="sales-history-format"
                  className="dashboard-ops-select"
                  value={salesHistoryFormat}
                  onChange={event => setSalesHistoryFormat(event.target.value)}
                  disabled={salesHistoryLoading}
                >
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                  <option value="both">Both</option>
                </select>
                <button
                  className="btn"
                  type="button"
                  onClick={handleSalesHistoryDownload}
                  disabled={!salesHistoryProject || salesHistoryLoading}
                >
                  {salesHistoryLoading ? 'Generating...' : 'Download'}
                </button>
              </div>
              {salesHistoryError && (
                <div className="dashboard-sales-history-error">{salesHistoryError}</div>
              )}
            </div>
            {canUseSalesManagement && (
              <button
                className="btn"
                type="button"
                onClick={() => onNavigate?.('sales')}
              >
                Sales Management
              </button>
            )}
            {canUseWeeklySalesForm && (
              <button
                className="btn"
                type="button"
                onClick={() => onNavigate?.('weekly-sales-dashboard')}
              >
                Weekly Sales Dashboard
              </button>
            )}
            {canUseWeeklySalesForm && (
              <button
                className="btn"
                type="button"
                onClick={() => onNavigate?.('weekly-sales-export')}
              >
                Weekly Sales CSV Export
              </button>
            )}
          </div>
        </div>

        <div className="dashboard-card dashboard-card--reports">
          <h3>Reports</h3>
          <p>Mylar PDFs and analytics</p>
          <StrategicCountdownTable />
          <div className="dashboard-card-actions">
            <button
              className="btn"
              type="button"
              onClick={() => onNavigate?.('reports')}
            >
              Reports
            </button>
          </div>
        </div>
      </div>

      {showProjectionsModal && (
        <CompletionProjectionsModal
          onClose={() => setShowProjectionsModal(false)}
        />
      )}
    </div>
  )
}

export default Dashboard

